import { useEffect, useState } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import flags from 'react-phone-number-input/flags';
import esLabels from "react-phone-number-input/locale/es.json";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { useForm, ValidationError } from '@formspree/react';
import {
  FaHandHoldingMedical,
  FaRegCircleCheck,
  FaPhone,
  FaAt,
  FaPaperPlane,
  FaAddressCard,
  FaClock,
  FaCakeCandles,
  FaMessage,
  FaUserGraduate,
  FaAddressBook,
  FaFilePdf,
  FaPersonCircleQuestion,
  FaUpload
} from "react-icons/fa6";
import { FiAlertCircle } from "react-icons/fi";
import { IoCalendarNumber, IoCloseSharp } from "react-icons/io5";
import "./Modal.css";
import { gform } from "../constants";
import { lockScroll, unlockScroll } from "../utils/lockScroll";

const FORM_ID = process.env.REACT_APP_SUBCRIPTION_FORM || "mpwydebe" as string;
const clip_anim: Variants = {
  initial: {
    clipPath: "circle(0 at 0% 50%)",
  },
  enter: {
    clipPath: "circle(140% at 0 14%)",
    transition: { duration: 1, ease: "easeInOut" },
  },
  exit: {
    clipPath: "circle(0 at 0% 50%)",
    transition: { duration: 0.8, ease: "easeInOut" },
  },
};

function flagEmoji(iso2: string) {
  return iso2
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(0x1f1e6 + (c.charCodeAt(0) - 65)));
}

const labelsWithFlags: Record<string, string> = Object.fromEntries(
  Object.entries(esLabels).map(([iso2, name]) => [iso2, `${flagEmoji(iso2)} ${name}`])
);

type ModalProps = {
  isOpen: boolean;
  handleClose: () => void;
};

const Modal: React.FC<ModalProps> = ({ isOpen, handleClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    age: '',
    dateofbirth: '',
    preferredTime: '',
    serviceType: '',
    civil: '',
    referred: '',
    profesionaltitle: ''
  });
  const [phone, setPhone] = useState<string>("");
  const valid = !phone || isValidPhoneNumber(phone);

  const [state, handleSubmit] = useForm(FORM_ID);
  const [sentOnce, setSentOnce] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    if (!isValidPhoneNumber(phone)) return;
    if ((fd.get('website') as string)?.trim()) return;

    formData.phone = phone;
    console.log(FORM_ID)
    try {
      await handleSubmit(e);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        age: '',
        dateofbirth: '',
        preferredTime: '',
        serviceType: '',
        civil: '',
        referred: '',
        profesionaltitle: ''
      });

    } catch (error) {
      if (!state?.errors?.kind) {
        setSentOnce(true);
        form.reset();
      }

    } finally {
      setTimeout(() => console.log(state), 5000);
    }
  };
  const { body } = document;

  useEffect(() => {
    if (!isOpen) return;

    body.style.overflow = "hidden";
    lockScroll()

    return () => {
      body.style.overflow = "visible";
      unlockScroll()
    };
  }, [isOpen, body.style]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="modal-wrapper"
          style={
            {
              WebkitClipPath: "circle(0 at 0% 50%)",
            } as React.CSSProperties
          }
          variants={clip_anim}
          initial="initial"
          animate="enter"
          exit="exit"
          onClick={handleClose}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button onClick={handleClose} className="close-button"><IoCloseSharp size={20} /></button>
            <div className="contact-form-section">
              <div className="form-header">
                <h2>Ficha de Inscripcion</h2>
              </div>
              <form onSubmit={onSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    <FaAt size={18} />
                    Email:
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    placeholder="tucorreo@ejemplo.com"
                  />
                  <ValidationError prefix="Email" field="email" errors={state.errors} />
                </div>
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    <FaAddressCard size={18} />
                    Nombre Completo:
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    placeholder="Emily Gamarro"
                  />
                  <ValidationError prefix="Name" field="name" errors={state.errors} />
                </div>
                <div className="form-group">
                  <label htmlFor="age" className="form-label">
                    <FaCakeCandles size={18} />
                    Fecha de Nacimiento:
                  </label>
                  <input
                    type="date"
                    id="dateofbirth"
                    name="dateofbirth"
                    value={formData.dateofbirth}
                    onChange={handleInputChange}
                    className="form-input date-field"
                    required
                    placeholder="tucorreo@ejemplo.com"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="age" className="form-label">
                    <IoCalendarNumber size={18} />
                    Edad:
                  </label>
                  <input
                    id="age"
                    name="age"
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={3}
                    value={formData.age}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    placeholder="20"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="civil" className="form-label">
                    <FaPersonCircleQuestion size={18} />
                    Estado Civil:
                  </label>
                  <select
                    id="civil"
                    name="civil"
                    value={formData.civil}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="" disabled hidden> Estado civil</option>
                    <option value="Soltero/a">Soltero/a</option>
                    <option value="Casado/a">Casado/a</option>
                    <option value="Viudo/a">Viudo/a</option>
                    <option value="Divorciado/a">Divorciado/a</option>
                    <option value="Unión Libre/Marital de Hecho">Unión Libre/Marital de Hecho</option>
                  </select>
                  <ValidationError prefix="Civil" field="civil" errors={state.errors} />
                </div>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    <FaPhone size={18} />
                    Teléfono
                  </label>
                  <PhoneInput
                    value={phone}
                    onChange={(v) => setPhone(v || "")}
                    defaultCountry="GT"
                    flags={flags}
                    countries={["GT", "MX", "US"]}
                    international
                    placeholder="+502 XXXX XXXX"
                    labels={labelsWithFlags}
                    className="form-input"
                  />
                  {!valid && (
                    <p className="wrong-number">Número inválido. </p>
                  )}
                  <ValidationError prefix="Phone" field="phone" errors={state.errors} />
                </div>
                <div className="form-group">
                  <label htmlFor="profesionaltitle" className="form-label">
                    <FaUserGraduate size={18} />
                    Título ó último grado cursado:
                  </label>
                  <input
                    type="text"
                    id="profesionaltitle"
                    name="profesionaltitle"
                    value={formData.profesionaltitle}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    placeholder="Universidad"
                  />
                  <ValidationError prefix="Profesionaltitle" field="profesionaltitle" errors={state.errors} />
                </div>
                <div className="form-group">
                  <label htmlFor="referred" className="form-label">
                    <FaAddressBook size={18} />
                    Referido por (opcional):
                  </label>
                  <input
                    type="text"
                    id="referred"
                    name="referred"
                    value={formData.referred}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Emily Gamarro"
                  />
                  <ValidationError prefix="Referred" field="referred" errors={state.errors} />
                </div>
                <div className="form-group">
                  <label htmlFor="serviceType" className="form-label">
                    <FaHandHoldingMedical size={18} />
                    Tipo de servicio
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="" disabled hidden> Dime en que servicio estas interesado</option>
                    <option value="Terapia Individual">Psicoterapia Individual</option>
                    <option value="Terapia de Pareja">Terapia Conductual Integrativa de Pareja</option>
                    <option value="Terapia Familiar">Psicoterapia en Cuidado Paliativos</option>
                    <option value="Evaluación Psicológica">Terapia Centrada en el Trauma</option>
                    <option value="Terapia Online">Taller</option>
                    <option value="Coaching Psicológico">Servicios Empresariales</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    <FaMessage size={18} />
                    Motivo de Consulta
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-textarea"
                    required
                    rows={4}
                    maxLength={265}
                    placeholder="Describe tus molestias, síntomas o razones por las cuales solicitas mi apoyo. Cuéntame los detalles, por favor."
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="preferredTime" className="form-label">
                    <FaClock size={18} />
                    Horario de Preferencia:
                  </label>
                  <select
                    id="preferredTime"
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">¿Cuál es tu horario preferido?</option>
                    <option value="Mañana (9:00-12:00)">Mañana (9:00-12:00)</option>
                    <option value="Mediodía (12:00-15:00)">Mediodía (12:00-15:00)</option>
                    <option value="Tarde (15:00-18:00)">Tarde (15:00-18:00)</option>
                    <option value="Noche (18:00-21:00)">Noche (18:00-21:00)</option>
                    <option value="Sábado mañana">Sábado Mañana</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="pdf" className="form-label">
                    <FaFilePdf size={18} />
                    Subir Consentimiento Informado (PDF)
                  </label>
                  <a
                    href={gform}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-whatsapp send-file"
                  >
                    <FaUpload className="icon" size={25} /> Subir Documento
                  </a>
                </div>
                {state.succeeded && sentOnce && (
                  <motion.div className="alert alert-success" initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      duration: 0.4,
                      scale: { type: 'spring', visualDuration: 0.4, bounce: 0.5 }
                    }}>
                    <FaRegCircleCheck size={20} />
                    <span>Muchas gracias por brindarnos la información anterior, esto permitirá evaluar el caso oportunamente, la misma será utilizada de manera
                      ESTRICTAMENTE CONFIDENCIAL.
                    </span>
                  </motion.div>
                )}

                {!!state?.errors?.kind && (
                  <motion.div className="alert alert-error" initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      duration: 0.4,
                      scale: { type: 'spring', visualDuration: 0.4, bounce: 0.5 }
                    }}>
                    <FiAlertCircle size={20} />
                    <span>Hubo un error al enviar el mensaje. Inténtalo nuevamente.</span>
                  </motion.div>
                )}
                <button
                  type="submit"
                  className="btn btn-primary form-submit"
                  disabled={state.submitting || !valid || !phone}
                >
                  {state.submitting ? (
                    <>Enviando...</>
                  ) : (
                    <>
                      <FaPaperPlane className="icon" />
                      Enviar Ficha de Inscripcion
                    </>
                  )}
                </button>
                <p className='data-protection'>Estrictamente Confidencial.</p>
              </form>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default Modal;
